import React, { FormEvent, useEffect, useState } from 'react';
import FormPage from 'components/form-page/FormPage';
import FormSummary from 'components/form-page/FormSummary';
import { moneyFormat } from 'helpers/moneyFormat';
import * as yup from 'yup';
import { api } from 'services/api';
import { toast } from 'react-toastify';
import history from 'services/history';
import InsideSaving from 'components/loading/InsideSaving';
import { Guide, GuidePaymentMethod, GuideTypeForm } from 'types/guide';
import { useGuideValidation } from './validation/useGuideValidation';
import GuideForm from './form/GuideForm';
import { useSelector } from 'store/selector';
import { User } from 'types/user';
import { format } from 'date-fns';
import { PaymentMethod } from 'types/paymentMethod';

const NewGuide: React.FC = () => {
  const [validation, setValidation, validate] = useGuideValidation();
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const user = useSelector(state => state.user);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<GuidePaymentMethod>({
    id: 0,
    guide_id: 0,
    payment_method_id: 0,
    installments: 1,
    value_total: 0,
    value_installment: 0,
    active: true,
    payment_method: {} as PaymentMethod,
  });
  const [guide, setGuide] = useState<GuideTypeForm>({
    appointment_date: null,
    doctor: null,
    patient: null,
    patient_id: null,
    doctor_id: null,
    marker_id: null,
    procedure_id: null,
    payment_methods: [],
    procedure: null,
    value_marker: 0,
    value_discount: 0,
    value_procedure: 0,
    value_total: 0,
    observation: '',
  });

  useEffect(() => {
    if (user) {
      setGuide(prev => ({
        ...prev,
        created_by: user.name,
      }));
    }
  }, [user]);

  useEffect(() => {
    setLoadingPaymentMethods(true);

    api
      .get('/payment-methods/all')
      .then(response => {
        setPaymentMethods(response.data);
      })
      .catch(() => {
        toast.error('Não foi possível carregar os métodos de pagamento');
      })
      .finally(() => setLoadingPaymentMethods(false));
  }, []);

  useEffect(() => {
    setLoadingUsers(true);

    api
      .get<User[]>('/users/all', { params: { name: '' } })
      .then(response => {
        setUsers(response.data);
      })
      .catch(() => {
        toast.error('Não foi possível carregar os usuários');
      })
      .finally(() => setLoadingUsers(false));
  }, []);

  const guideTotal = guide.procedure?.sum_commission_to_total
    ? guide.value_procedure + guide.value_marker - guide.value_discount
    : guide.value_procedure - guide.value_discount;

  function handleChange(index: keyof GuideTypeForm, value: any) {
    setGuide(prev => ({ ...prev, [index]: value }));
  }

  function handleChangePayment(index: keyof GuidePaymentMethod, value: any) {
    setPaymentMethod(prev => ({ ...prev, [index]: value }));
  }

  function handleSubmit() {
    setSaving(true);

    api
      .post('/guides', {
        ...guide,
        payment_methods: [
          {
            ...paymentMethod,
            value_installment: guide.procedure?.sum_commission_to_total
              ? guide.value_procedure + guide.value_marker - guide.value_discount
              : guide.value_procedure - guide.value_discount,
            installments: 1,
            value_total: guide.procedure?.sum_commission_to_total
              ? guide.value_procedure + guide.value_marker - guide.value_discount
              : guide.value_procedure - guide.value_discount,
          },
        ],
        sum_commission_to_total: guide.procedure?.sum_commission_to_total,
        appointment_date: guide.appointment_date ? format(guide.appointment_date, 'yyyy-MM-dd HH:mm:ss') : null,
      })
      .then(response => {
        toast.success('Guia cadastrada com sucesso!');
        history.push(`/guides/${response.data.id}/print`);
      })
      .catch(err => {
        toast.error(err.response.data.error || 'Não foi possível cadastrar a guia');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    validate(guide, paymentMethod)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        console.error(err);
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  return (
    <FormPage
      title="Cadastro de guia"
      onClose={() => history.push('/guides')}
      onSubmit={handleValidation}
      aside={
        <FormSummary
          title="Resumo da guia"
          items={[
            { label: 'Valor da consulta', value: moneyFormat(guide.value_procedure || 0) },
            { label: 'Desconto', value: `- ${moneyFormat(guide.value_discount || 0)}` },
          ]}
          totalLabel="Total da guia"
          total={moneyFormat(Math.max(guideTotal || 0, 0))}
          submitLabel="Salvar guia"
          submitting={saving}
          notice="Certifique-se de que todos os valores financeiros estão corretos antes de salvar."
        />
      }
    >
      {saving && <InsideSaving />}

      <GuideForm
        loadingPaymentMethods={loadingPaymentMethods}
        paymentMethods={paymentMethods}
        paymentMethod={paymentMethod}
        users={users}
        loadingUsers={loadingUsers}
        handleChangePayment={handleChangePayment}
        handleChange={handleChange}
        guide={guide}
        validation={validation}
      />
    </FormPage>
  );
};

export default NewGuide;
